// Shared Google Maps marker helpers — used by both PropertyViewer.js's Units
// map and UnitMiningWizard.js's Discovery Review map.

import React, { useEffect, useState } from "react";
import { useGoogleMap } from "@react-google-maps/api";

// google.maps.Marker is deprecated in favor of AdvancedMarkerElement, which
// @react-google-maps/api has no built-in wrapper for — this thin component
// creates/updates/tears down a real AdvancedMarkerElement imperatively
// against the map from context, and hands it to a nested <InfoWindow> as its
// anchor (the same way the library's own <Marker> does internally).
export const AdvancedMarker = ({ position, content, title, onClick, children }) => {
	const map = useGoogleMap();
	const [marker, setMarker] = useState(null);

	useEffect(() => {
		if (!map || !window.google?.maps?.marker?.AdvancedMarkerElement) return undefined;
		// gmpClickable isn't reliably on by default for markers with custom
		// content — without it, gmp-click never fires no matter what's listening.
		const instance = new window.google.maps.marker.AdvancedMarkerElement({ map, position, content, title, gmpClickable: true });
		setMarker(instance);
		return () => {
			instance.map = null;
		};
		// Only the map itself should recreate the marker; position/content/title
		// updates below are applied to the existing instance instead.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [map]);

	useEffect(() => {
		if (marker) marker.position = position;
	}, [marker, position]);

	useEffect(() => {
		if (marker) marker.content = content;
	}, [marker, content]);

	useEffect(() => {
		if (marker) marker.title = title;
	}, [marker, title]);

	useEffect(() => {
		if (!marker || !onClick) return undefined;
		// AdvancedMarkerElement is a real DOM custom element — use its native
		// "gmp-click" event instead of the legacy Marker addListener('click').
		marker.addEventListener("gmp-click", onClick);
		return () => marker.removeEventListener("gmp-click", onClick);
	}, [marker, onClick]);

	if (!marker) return null;
	return React.Children.map(children, (child) => (child ? React.cloneElement(child, { anchor: marker }) : child));
};

// Builds the small circular DOM pin used for clustered markers — a plain
// color+label circle, larger when a count badge is shown for a cluster.
// `selected` adds a blue ring — used by UnitMiningWizard's map-select mode;
// callers that never pass it (PropertyViewer's own Units map) are unaffected.
export const buildUnitPinElement = ({ color, label, diameter, selected }) => {
	const el = document.createElement("div");
	el.style.width = `${diameter}px`;
	el.style.height = `${diameter}px`;
	el.style.borderRadius = "50%";
	el.style.background = color;
	el.style.border = selected ? "3px solid #184c93" : "2px solid #ffffff";
	el.style.boxShadow = selected ? "0 0 0 3px rgba(24, 76, 147, 0.35), 0 1px 3px rgba(0, 0, 0, 0.4)" : "0 1px 3px rgba(0, 0, 0, 0.4)";
	el.style.display = "flex";
	el.style.alignItems = "center";
	el.style.justifyContent = "center";
	el.style.color = "#ffffff";
	el.style.fontSize = `${Math.max(10, Math.round(diameter * 0.42))}px`;
	el.style.fontWeight = "700";
	el.style.fontFamily = "inherit";
	el.textContent = label;
	return el;
};
